import { createInertiaApp } from '@inertiajs/react';
import { createElement } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import { GlobalErrorBoundary } from '@/components/global-error-boundary';
import { ThemeProvider } from '@/components/theme-provider';
import { resolvePage, type ResolvedComponent } from '@/lib/resolve-page';

import '@/stylesheets/application.css';

createInertiaApp({
  progress: {
    color: '#FFEC40',
    showSpinner: false,
  },
  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
      eager: true,
    });
    return resolvePage(name, pages);
  },
  setup({ el, App, props }) {
    if (!el) {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you loaded Inertia.js on a non-Inertia page.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      );
      return;
    }

    // Mirrors the Next root layout: theme class on <html> and the app/global-error.tsx
    // boundary around the whole tree. Matches the tree ssr/ssr.tsx renders server-side,
    // so an SSR'd page hydrates instead of discarding the server markup.
    const app = createElement(
      GlobalErrorBoundary,
      null,
      createElement(
        ThemeProvider,
        {
          attribute: 'class',
          defaultTheme: 'dark',
          enableSystem: true,
          disableTransitionOnChange: true,
        },
        createElement(App, props),
      ),
    );

    if (el.hasChildNodes()) {
      hydrateRoot(el, app);
    } else {
      createRoot(el).render(app);
    }

    // Sonner's Toaster is client-only chrome that ssr/ssr.tsx deliberately leaves out of
    // the rendered tree, so it gets its own root instead of hydrating alongside `app`.
    let toasterEl = document.getElementById('toaster-root');
    if (!toasterEl) {
      toasterEl = document.createElement('div');
      toasterEl.id = 'toaster-root';
      document.body.appendChild(toasterEl);
    }
    createRoot(toasterEl).render(createElement(Toaster, { position: 'bottom-right' }));
  },
});
